/**
 * apps/command/src/screens/DavomatScreen.tsx — §7's live attendance board.
 *
 * ============================================================================
 * IT LEADS WITH COVERAGE, NOT WITH A HEADCOUNT.
 * ============================================================================
 *
 * §7.10: «Bu mahallada hozir hech kim yoʻq» is "the fact a governor actually
 * needs during reception hours". A board that opens with "182 of 225 present"
 * has that fact in it and buries it — the three mahallas where a citizen will
 * arrive to a locked door are three rows somewhere in a list of thirty-three.
 *
 * So uncovered mahallas come first, in their own block, and the percentage is
 * a smaller number underneath. §9.1's exception-first framing, applied to
 * people instead of machines.
 *
 * ============================================================================
 * THREE STATES, AND THE MIDDLE ONE IS THE POINT.
 * ============================================================================
 *
 * §7.3: mahalla staff are SUPPOSED to be out among residents, and a system that
 * records «mahalla aylanish» as absence "will be hated and gamed within a
 * week". So `away_working` is rendered as a positive state in its own colour —
 * not as a shade of absent, and never folded into a single "present" figure
 * with `at_desk`, because a citizen at the door cares about the difference.
 *
 * ============================================================================
 * IT SHOWS NO ARRIVAL TIMES ON THE OVERVIEW.
 * ============================================================================
 *
 * The API sends `since` and this screen uses it only for how long the CURRENT
 * state has lasted, never as a clock somebody scans down a column to find who
 * was late. §7.5's late detection is P4, it has a grace period, and it belongs
 * beside a conversation rather than in a list a room full of people can read
 * over somebody's shoulder.
 */
import { useCallback, useEffect, useState } from 'react';
import type { components } from '@navoiy/api-types';
import { api, ApiError, unwrap } from '../api/client.js';
import { useSession } from '../auth/SessionContext.js';
import { hasKey, t } from '../i18n/index.js';
import { EmptyState, Notice, Panel, PanelHeader, Skeleton } from '../ui/index.jsx';
import { KpiCard, PageHeader } from '../ui/console.jsx';
import { BarChart, StatusDonut } from '../ui/charts.jsx';

type MbtStaffPresence = components['schemas']['MbtStaffPresence'];
type MbtAttendanceAnomaly = components['schemas']['MbtAttendanceAnomaly'];

interface MahallaBoard {
  mahalla_id: string;
  mahalla_name: string;
  at_desk: number;
  away_working: number;
  total: number;
  uncovered: boolean;
  members?: MbtStaffPresence[];
}

function roleLabel(role: string): string {
  return hasKey(`staff.role.${role}`) ? t(`staff.role.${role}`) : role;
}

/** The three states, each in the colour that says what it means. */
/** Falls back to the raw value, the way every other MBT screen does. */
function presenceLabel(presence: string): string {
  return hasKey(`davomat.presence.${presence}`) ? t(`davomat.presence.${presence}`) : presence;
}

function modeLabel(mode: string): string {
  return hasKey(`davomat.mode.${mode}`) ? t(`davomat.mode.${mode}`) : mode;
}

function presenceTone(presence: string): string {
  if (presence === 'at_desk') return 'text-ok';
  if (presence === 'away_working') return 'text-brand';
  return 'text-ink-500';
}

/**
 * The hours a hokimlik building is normally staffed.
 *
 * ============================================================================
 * A DISPLAY CONVENTION, AND SAID TO BE ONE. NOT A SCHEDULE THE SYSTEM KNOWS.
 * ============================================================================
 *
 * §7.5's shift templates are P4 and are not built, and the anomaly engine
 * deliberately refuses to approximate them — `outside_shift_window` is on
 * `ATTENDANCE_ANOMALY_RULES`' not-shipping list precisely because guessing
 * 09:00–18:00 across 33 mahallas would accuse every early cleaner and every
 * late reception evening of something.
 *
 * That reasoning is about ACCUSING A PERSON. This is a caveat on a FIGURE, and
 * the opposite error is just as real: at 21:00 this board reads «0 at desk, 32
 * mahallas uncovered» in red, and a reader who does not check the clock
 * concludes the province has walked out. Nobody is flagged by the line below;
 * it only says that a low count at this hour is unremarkable, and it says out
 * loud that it is a rule of thumb.
 *
 * Read from the BROWSER's clock, which for this panel's users is Tashkent.
 */
/** How many uncovered mahallas to name before falling back to a count. */
const UNCOVERED_SHOWN = 8;

const OFFICE_OPENS_HOUR = 9;
const OFFICE_CLOSES_HOUR = 18;

export function DavomatScreen(): React.JSX.Element {
  const { expire } = useSession();
  const onExpired = useCallback(() => expire(), [expire]);

  const [board, setBoard] = useState<MahallaBoard[] | null>(null);
  const [present, setPresent] = useState<number | null>(null);
  const [anomalies, setAnomalies] = useState<MbtAttendanceAnomaly[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = unwrap(await api().GET('/mbt/attendance/board'));
      setBoard(data.mahallas as MahallaBoard[]);
      setPresent(data.staff_present);
      /**
       * §7.8's queue, on the same screen and BELOW the board.
       *
       * Not a separate nav entry, and deliberately not above the coverage
       * block: a screen that opens with a list of colleagues to look into is a
       * surveillance tool, and §7.8's whole framing is that these are items to
       * review, not people to watch. Coverage first, then the queue.
       */
      const queue = unwrap(await api().GET('/mbt/attendance/anomalies', {}));
      setAnomalies(queue.items as MbtAttendanceAnomaly[]);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onExpired();
      else setError(t('error.network'));
    }
  }, [onExpired]);

  useEffect(() => {
    void load();
    // §15.9's "live without refresh". Thirty seconds: a check-in is a person
    // walking through a door, not a CPU sample.
    const timer = setInterval(() => void load(), 30_000);
    return () => clearInterval(timer);
  }, [load]);

  /**
   * A FAILED FIRST LOAD MUST NOT LOOK LIKE A SLOW ONE.
   *
   * `board` stays null when the first request fails, and returning the skeleton
   * unconditionally would leave somebody staring at a loading state forever
   * with the reason already in `error` and nowhere to render it. §15.12's
   * skeletons are for waiting, not for failing.
   */
  if (board === null) {
    return error ? <Notice tone="warn">{error}</Notice> : <Skeleton className="h-64" />;
  }

  const nowHour = new Date().getHours();
  const withinOfficeHours = nowHour >= OFFICE_OPENS_HOUR && nowHour < OFFICE_CLOSES_HOUR;

  const uncovered = board.filter((entry) => entry.uncovered);
  const totalStaff = board.reduce((sum, entry) => sum + entry.total, 0);
  const away = board.reduce((sum, entry) => sum + entry.away_working, 0);

  /**
   * Presence per mahalla, as a table rather than a list of cards.
   *
   * §7.10's question is comparative — which mahalla is short-staffed right now
   * — and the three states (`at_desk`, `away_working`, `off`) only mean
   * something against each other and against the mahalla's total. Three
   * columns and a bar answer that in one pass; three counts in prose do not.
   */
  const rows = [...(board ?? [])].sort(
    (a, b) =>
      Number(b.uncovered) - Number(a.uncovered) ||
      a.at_desk / (a.total || 1) - b.at_desk / (b.total || 1),
  );

  const atDeskTotal = rows.reduce((sum, row) => sum + row.at_desk, 0);
  const awayTotal = rows.reduce((sum, row) => sum + row.away_working, 0);
  const staffTotal = rows.reduce((sum, row) => sum + row.total, 0);

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow={<span>{t('davomat.eyebrow')}</span>}
        title={t('nav.davomat')}
        subtitle={t('davomat.subtitle')}
        right={
          <p className="text-hero font-bold tabular-nums leading-none text-ink-100">
            {atDeskTotal}
          </p>
        }
      />

      {error ? <Notice tone="warn">{error}</Notice> : null}

      {/*
        THE CLOCK, BEFORE THE NUMBERS.

        Everything below is «right now», and «right now» is the missing half of
        every figure on this board: 0 at desk at 07:00 is a building that has
        not opened, and 0 at desk at 14:00 is an emergency. The screen used to
        render those two identically, in red.
      */}
      <Notice tone={withinOfficeHours ? 'quiet' : 'quiet'}>
        {withinOfficeHours
          ? t('davomat.withinHours', {
              from: `0${OFFICE_OPENS_HOUR}:00`,
              to: `${OFFICE_CLOSES_HOUR}:00`,
            })
          : t('davomat.outsideHours', {
              from: `0${OFFICE_OPENS_HOUR}:00`,
              to: `${OFFICE_CLOSES_HOUR}:00`,
            })}
      </Notice>

      {/*
        FOUR NUMBERS, at the top, where this screen's own three used to sit
        halfway down under a donut and a table. «How many people are at their
        desks right now» is the question somebody opens this page with.
      */}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label={t('davomat.atDesk')}
          /* `present` may be null, and 0 would be a claim: a deployment whose
             staff have never checked in has nothing to count, and §15.10's
             «hali yoʻq» is the honest rendering of that. */
          value={present === null ? null : String(present)}
          absent={t('app.notYet')}
          tone={present !== null && present > 0 ? 'ok' : 'neutral'}
          footerLabel={t('davomat.staffTotal')}
          footerValue={String(totalStaff)}
        />
        <KpiCard
          label={t('davomat.awayWorking')}
          value={String(away)}
          absent={t('app.notYet')}
          tone="info"
          footerLabel={t('davomat.awayMeaning')}
          footerValue=""
        />
        <KpiCard
          label={t('davomat.absent')}
          value={String(Math.max(0, totalStaff - (present ?? 0) - away))}
          absent={t('app.notYet')}
          tone="neutral"
          footerLabel={
            withinOfficeHours ? t('davomat.absentMeaning') : t('davomat.absentOutside')
          }
          footerValue=""
        />
        <KpiCard
          label={t('davomat.uncoveredKpi')}
          value={String(uncovered.length)}
          absent={t('app.notYet')}
          /* Outside office hours an uncovered mahalla is not a fault, so it is
             not painted as one. The count is the same; the judgement is not. */
          tone={uncovered.length === 0 ? 'ok' : withinOfficeHours ? 'crit' : 'neutral'}
          footerLabel={t('davomat.ofMahallas', { n: String(rows.length) })}
          footerValue=""
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-[1fr_2fr]">
        <Panel>
          <PanelHeader title={t('davomat.split')} />
          <div className="px-4 py-4">
            {/*
              THREE STATES, NEVER A BOOLEAN. «At desk» and «out among residents»
              are both working — §7.3 is explicit that mahalla staff are
              SUPPOSED to be out — and a present/absent donut would mark half a
              working day as absence.
            */}
            <StatusDonut
              centreValue={String(atDeskTotal)}
              centreLabel={t('davomat.atDesk')}
              slices={[
                { key: 'desk', label: t('davomat.atDesk'), value: atDeskTotal, className: 'text-ok' },
                { key: 'away', label: t('davomat.awayWorking'), value: awayTotal, className: 'text-info' },
                {
                  key: 'off',
                  label: t('davomat.presence.off'),
                  value: Math.max(0, staffTotal - atDeskTotal - awayTotal),
                  className: 'text-offline',
                },
              ].filter((slice) => slice.value > 0)}
            />
          </div>
        </Panel>

        <Panel>
          <PanelHeader title={t('davomat.coverageChart')} aside={t('davomat.coverageChartHint')} />
          <div className="px-4 py-4">
            <BarChart
              bars={rows.slice(0, 12).map((row) => ({
                key: row.mahalla_id,
                label: row.mahalla_name.slice(0, 8),
                value: row.at_desk,
                highlight: row.uncovered,
              }))}
            />
          </div>
        </Panel>
      </div>

      <Panel>
        <PanelHeader title={t('davomat.byMahalla')} aside={t('davomat.byMahallaHint')} />
        {board === null ? (
          <div className="px-4 py-4">
            <Skeleton className="h-48" />
          </div>
        ) : (
          /*
            ==================================================================
            A GRID OF SMALL TILES, NOT A THIRTY-THREE ROW TABLE.
            ==================================================================

            §7.10's question is comparative — WHICH mahalla is short-staffed
            right now — and a table answers it by making somebody read down a
            column of thirty-three. Six columns of tiles put the whole province
            in two screens' worth of glance, which is the reading this board is
            actually used for: scanning for the one that is wrong.

            Each tile carries the four things that decide it and nothing else:
            the name, the fraction, a coverage bar, and — only when it is not
            zero — the count working away from the desk. A tile with eight
            fields on it is a table row with a border.

            The totals row goes with the table, and is not lost: the four KPI
            cards at the top of this screen are the same figures, larger, where
            somebody looks first.
          */
          <div className="grid grid-cols-3 gap-2 p-4 sm:grid-cols-4 lg:grid-cols-6 2xl:grid-cols-8">
            {rows.map((row) => {
              const pct = row.total > 0 ? (row.at_desk / row.total) * 100 : 0;
              /* Crit ONLY during the working day — the same rule the KPI card
                 and the uncovered panel follow. An unstaffed mahalla at 21:00
                 is a closed building. */
              const alarm = row.uncovered && withinOfficeHours;
              return (
                <div
                  key={row.mahalla_id}
                  className={`flex flex-col gap-1 overflow-hidden rounded border bg-panel px-2 py-1.5 ${
                    alarm ? 'border-crit/40' : 'border-hairline'
                  }`}
                  title={row.mahalla_name}
                >
                  <p
                    className={`truncate text-nano font-semibold uppercase tracking-wide ${
                      alarm ? 'text-crit' : 'text-ink-500'
                    }`}
                  >
                    {row.mahalla_name}
                  </p>

                  <p className="flex items-baseline justify-between gap-1 tabular-nums">
                    <span className="flex items-baseline gap-0.5">
                      <span
                        className={`text-label font-bold leading-none ${
                          row.at_desk > 0 ? 'text-ok' : alarm ? 'text-crit' : 'text-ink-300'
                        }`}
                      >
                        {row.at_desk}
                      </span>
                      <span className="text-nano text-ink-500">/{row.total}</span>
                    </span>
                    {/* Only when there is something to say. A «0» on every tile
                        is thirty-three zeros the eye has to filter out. */}
                    {row.away_working > 0 ? (
                      <span className="text-nano text-info">+{row.away_working}</span>
                    ) : null}
                  </p>

                  <div className="h-1 overflow-hidden rounded-full bg-hairline">
                    <div
                      className={`h-full rounded-full ${row.at_desk > 0 ? 'bg-ok' : 'bg-hairline'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Panel>

      {/*
        UNCOVERED FIRST. §7.10's whole point, and the reason this block is above
        the figures rather than a filter somebody has to think to apply.
      */}
      {/*
        ==================================================================
        SHOWN ONLY DURING OFFICE HOURS, AND CAPPED.
        ==================================================================

        This panel listed every uncovered mahalla in full — which, at 21:00,
        is all thirty-two of them, one per row, immediately below a table
        that had just listed the same thirty-two. Two identical walls of red
        for a fact that is not a fault at that hour.

        §7.10's point survives intact: when a mahalla is unstaffed DURING THE
        WORKING DAY somebody needs to know, and then this is the loudest
        thing on the page. Outside those hours the table still carries every
        row for anybody who wants it; what is removed is the second copy and
        the alarm colour.

        The cap is the other half. A list of eight names is read; a list of
        thirty-two is scrolled past, and the count in the header is the fact
        anyway.
      */}
      {withinOfficeHours && uncovered.length > 0 ? (
        <Panel>
          <PanelHeader title={t('davomat.uncoveredTitle')} aside={String(uncovered.length)} />
          <div className="px-4 py-3">
            <p className="mb-2 text-micro text-ink-500">{t('davomat.uncoveredNote')}</p>
            <ul className="divide-y divide-hairline">
              {uncovered.slice(0, UNCOVERED_SHOWN).map((entry) => (
                <li key={entry.mahalla_id} className="flex justify-between py-2 text-label">
                  <span className="text-crit">{entry.mahalla_name}</span>
                  <span className="tabular-nums text-ink-500">
                    {t('davomat.ofTotal', { n: 0, total: entry.total })}
                  </span>
                </li>
              ))}
            </ul>
            {uncovered.length > UNCOVERED_SHOWN ? (
              <p className="mt-2 text-micro text-ink-500">
                {t('davomat.uncoveredMore', {
                  n: String(uncovered.length - UNCOVERED_SHOWN),
                })}
              </p>
            ) : null}
          </div>
        </Panel>
      ) : null}


      {/*
        §7.8's review queue. Read-only here: closing one needs a conclusion and
        a sentence, and that is `mbt.attendance.correct`'s HR screen rather than
        a button on a board a mahalla lead can also see.
      */}
      {anomalies.length > 0 ? (
        <Panel>
          <PanelHeader title={t('davomat.anomalies')} aside={String(anomalies.length)} />
          <div className="px-4 py-3">
            <p className="mb-2 text-micro text-ink-500">{t('davomat.anomaliesNote')}</p>
            <ul className="divide-y divide-hairline">
              {anomalies.map((entry) => (
                <li key={entry.id} className="flex items-start justify-between gap-4 py-2">
                  <div className="min-w-0">
                    <p className="text-label text-ink-100">{entry.full_name}</p>
                    <p className="text-micro text-ink-500">
                      {entry.mahalla_name} · {entry.observed_on}
                    </p>
                  </div>
                  <span className="shrink-0 text-micro text-warn">
                    {hasKey(`davomat.anomaly.${entry.rule}`)
                      ? t(`davomat.anomaly.${entry.rule}`)
                      : entry.rule}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </Panel>
      ) : null}

      <Panel>
        <PanelHeader title={t('davomat.title')} aside={t('davomat.subtitle')} />
        <div className="px-4 py-2">
          {board.length === 0 ? (
            <EmptyState message={t('davomat.empty')} />
          ) : (
            <ul className="divide-y divide-hairline">
              {board.map((entry) => (
                <li key={entry.mahalla_id}>
                  <button
                    type="button"
                    onClick={() => setOpen(open === entry.mahalla_id ? null : entry.mahalla_id)}
                    className="flex w-full items-center justify-between gap-4 py-2.5 text-left transition-colors duration-state hover:text-brand"
                  >
                    <span className="min-w-0 truncate text-label text-ink-100">
                      {entry.mahalla_name}
                    </span>
                    <span className="shrink-0 text-micro tabular-nums">
                      <span className={entry.at_desk > 0 ? 'text-ok' : 'text-crit'}>
                        {entry.at_desk}
                      </span>
                      {entry.away_working > 0 ? (
                        <span className="text-brand"> +{entry.away_working}</span>
                      ) : null}
                      <span className="text-ink-500"> / {entry.total}</span>
                    </span>
                  </button>

                  {open === entry.mahalla_id ? (
                    <ul className="pb-3 pl-3">
                      {(entry.members ?? []).map((member) => (
                        <li
                          key={member.member_id}
                          className="flex items-center justify-between gap-4 py-1 text-micro"
                        >
                          <span className="min-w-0 truncate text-ink-300">
                            {member.full_name}{' '}
                            <span className="text-ink-500">{roleLabel(member.role)}</span>
                          </span>
                          {/*
                            §7.5's missing check-out, in WARNING colour rather
                            than the neutral «Yoʻq». The person reads as off —
                            a check-in nobody closed is not somebody at work —
                            but this is a timesheet about to be wrong, and HR
                            closes it with §7.10's attributed correction.
                          */}
                          <span
                            className={`shrink-0 ${
                              member.stale_check_in ? 'text-warn' : presenceTone(member.presence)
                            }`}
                          >
                            {member.stale_check_in
                              ? t('davomat.staleCheckIn')
                              : presenceLabel(member.presence)}
                            {!member.stale_check_in &&
                            member.work_mode &&
                            member.work_mode !== 'office'
                              ? ` · ${modeLabel(member.work_mode)}`
                              : ''}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </Panel>
    </div>
  );
}
